import { useContext, useEffect, useMemo, useRef } from "react";

import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import { createLogger } from "@/logger";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import type { PreferredLanguage } from "@/models/languages";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { store } from "@/store/store";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { type AcknowledgementRequirement, DEFAULT_REQUIREMENT } from "./acknowledgements";
import { computeDispatchItems, type DispatchItem, dispatchItemCollections } from "./dispatchItems";

const log = createLogger("feed");

/** Stable fallback while the compute's first run is pending. */
const NO_ITEMS: DispatchItem[] = [];

/**
 * The items a message-grain Dispatch view lists, recomputed as the store changes.
 *
 * The driver's messages and TMS events, each when the view's Requires Acknowledgement list takes its kind. Off (an
 * empty, stable list) at the driver grain, so a driver-per-row view never pays for walking every item.
 *
 * The warmup loads items without their content, so a message row shown here downloads its own text and files once;
 * the row renders without text until they land.
 */
export function useDispatchItemRows(
  workspaceId: string,
  membershipId: string | undefined,
  grain: DispatchGrain,
  teamAliases: readonly string[] = [],
  requires: AcknowledgementRequirement = DEFAULT_REQUIREMENT,
): DispatchItem[] {
  const { myAccount } = useContext(MyAccountContext);
  const language = myAccount?.preferredLanguage ?? "en";
  const enabled = grain === "message";
  const teamAliasesKey = teamAliases.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the joined key is the aliases' identity
  const computed = useCollectionsCompute(
    dispatchItemCollections,
    () =>
      enabled && workspaceId && membershipId
        ? computeDispatchItems({
            workspaceId,
            membershipId,
            language,
            teamAliases,
            requires,
          })
        : null,
    [enabled, workspaceId, membershipId, language, teamAliasesKey, requires],
    "Dispatch:items",
  );
  const rows = useMemo(() => computed ?? NO_ITEMS, [computed]);

  // `${id}:${language}` keys already requested this mount, so a recompute while a download is in flight asks for
  // nothing twice, and the first visit to a language (which clears every item's loaded content) asks again; what an
  // earlier visit downloaded stays in the store. The effect keys on the joined ids, so the reset that clears loaded
  // content before the language changes does not fetch in the old language.
  const messageIdsKey = useMemo(
    () =>
      rows
        .filter((row) => row.source === "message")
        .map((row) => row.id)
        .join(","),
    [rows],
  );
  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (messageIdsKey === "") return;
    const missing = messageIdsKey
      .split(",")
      .filter((id) => !requested.current.has(`${id}:${language}`))
      .filter((id) => store.get("item", id)?.loadedContent !== true);
    if (missing.length === 0) return;
    const keys = missing.map((id) => `${id}:${language}`);
    for (const key of keys) requested.current.add(key);
    downloadFeedItemContent(workspaceId, missing, language as PreferredLanguage).catch((error: unknown) => {
      for (const key of keys) requested.current.delete(key);
      log.warn("[DISPATCH] Message content download failed; the next change retries", { error });
    });
  }, [messageIdsKey, workspaceId, language]);

  return rows;
}
